// Live session view (#212): the API's SSE audit stream for one agent session.
// The endpoint backfills existing rows then tails until the session's terminal
// row, so a finished session resolves straight to `done`.
//
// The loop itself is `lib/appendLogStream.ts` — shared with the fleet-run view,
// which reads the same backfill/tail/terminate shape over a different route. All
// this module owns is the row schema and the address.

import { SessionEventFrameSchema, SseEvent } from '@developerz/domain';
import { useAppendLogStream } from './appendLogStream';

/**
 * One `audit_events` row as `GET /v1/sessions/:id/events` streams it.
 *
 * The schema is the SHARED wire contract (`@developerz/domain`
 * `wire/sse-frames.ts`), not a copy: it was declared here AND as
 * `SessionEventView` in `packages/mcp` — which this bundle may not import — so
 * the producer's shape and the shape this view parses agreed by inspection
 * alone. `apps/api/src/routes/sse-frames.contract.test.ts` is what checks it now.
 */
export const auditEventRowSchema = SessionEventFrameSchema;

export type AuditEventRow = ReturnType<typeof auditEventRowSchema.parse>;

/**
 * Open the SSE stream for `sessionId` and expose the accumulating events, a
 * connection status and a `retry` that reconnects from a clean slate. A
 * mid-stream recycle reconnects by itself, resuming from the last row seen.
 */
export function useSessionStream(sessionId: () => string, token: string | null) {
  return useAppendLogStream(sessionId, token, {
    path: (id) => `/v1/sessions/${encodeURIComponent(id)}/events`,
    event: SseEvent.Audit,
    schema: auditEventRowSchema,
  });
}
